import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut,
  Mail,
  Phone,
  Trash2,
  Check,
  Plus,
  Pencil,
  X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import PegasusLogo from "@/components/PegasusMark";

const SECTOR_OPTS = [
  "Industrial",
  "Commercial",
  "Residential",
  "Institutional",
  "Hospitality",
];

const EMPTY_PROJECT = {
  title: "",
  sector: "Commercial",
  location: "",
  client: "",
  description: "",
  image_url: "",
  year: "",
  published: true,
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [projects, setProjects] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_PROJECT);
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [l, p] = await Promise.all([
        api.get("/leads"),
        api.get("/projects", { params: { published_only: false } }),
      ]);
      setLeads(l.data || []);
      setProjects(p.data || []);
    } catch {
      toast.error("Failed to load data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const markRead = async (id) => {
    await api.patch(`/leads/${id}/read`);
    setLeads((ls) =>
      ls.map((l) => (l.id === id ? { ...l, read: true } : l))
    );
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await api.delete(`/leads/${id}`);
    setLeads((ls) => ls.filter((l) => l.id !== id));
    toast.success("Lead deleted.");
  };

  const saveProject = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const { data } = await api.put(`/projects/${editing}`, form);
        setProjects((ps) => ps.map((p) => (p.id === editing ? data : p)));
        toast.success("Project updated.");
      } else {
        const { data } = await api.post("/projects", form);
        setProjects((ps) => [data, ...ps]);
        toast.success("Project created.");
      }
      setEditing(null);
      setForm(EMPTY_PROJECT);
    } catch (err) {
      toast.error("Save failed.");
    }
  };

  const editProject = (p) => {
    setEditing(p.id);
    setForm({
      title: p.title,
      sector: p.sector,
      location: p.location,
      client: p.client || "",
      description: p.description,
      image_url: p.image_url || "",
      year: p.year || "",
      published: p.published,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteProject = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    await api.delete(`/projects/${id}`);
    setProjects((ps) => ps.filter((p) => p.id !== id));
    toast.success("Deleted.");
  };

  const unreadCount = React.useMemo(
    () => leads.filter((l) => !l.read).length,
    [leads]
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 backdrop-blur-2xl bg-[#0A0A0A]/80 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <PegasusLogo className="h-10" />
            <span className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] border-l border-[#2A2A2A] pl-3">
              Admin
            </span>
          </Link>
          <div className="flex items-center gap-5">
            <span className="text-xs text-[#A3A3A3] hidden sm:block">
              {user?.email}
            </span>
            <button
              onClick={handleLogout}
              className="text-xs flex items-center gap-2 border border-[#2A2A2A] px-4 py-2 hover:border-[#D4AF37] hover:text-[#D4AF37]"
              data-testid="admin-logout"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center gap-2 mb-10 border-b border-[#2A2A2A]">
          {[
            { k: "leads", l: `Leads${unreadCount ? ` (${unreadCount})` : ""}` },
            { k: "projects", l: "Projects" },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`px-6 py-3 text-sm transition-colors border-b-2 -mb-px ${
                tab === t.k
                  ? "border-[#D4AF37] text-[#D4AF37]"
                  : "border-transparent text-[#A3A3A3] hover:text-white"
              }`}
              data-testid={`admin-tab-${t.k}`}
            >
              {t.l}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-[#A3A3A3]">Loading…</div>
        ) : tab === "leads" ? (
          <LeadsTable leads={leads} onRead={markRead} onDelete={deleteLead} />
        ) : (
          <ProjectsManager
            projects={projects}
            form={form}
            setForm={setForm}
            editing={editing}
            setEditing={setEditing}
            onSave={saveProject}
            onEdit={editProject}
            onDelete={deleteProject}
            onReset={() => {
              setEditing(null);
              setForm(EMPTY_PROJECT);
            }}
          />
        )}
      </div>
    </div>
  );
}

function LeadsTable({ leads, onRead, onDelete }) {
  if (!leads.length)
    return (
      <div
        className="border border-[#2A2A2A] bg-[#141414] p-16 text-center text-[#A3A3A3]"
        data-testid="leads-empty"
      >
        No enquiries yet.
      </div>
    );
  return (
    <div className="border border-[#2A2A2A] bg-[#141414]" data-testid="leads-table">
      <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 text-[11px] uppercase tracking-[0.25em] text-[#D4AF37] border-b border-[#2A2A2A]">
        <div className="col-span-3">Contact</div>
        <div className="col-span-3">Subject</div>
        <div className="col-span-4">Message</div>
        <div className="col-span-2 text-right">Actions</div>
      </div>
      {leads.map((l) => (
        <div
          key={l.id}
          className={`grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-6 border-b border-[#2A2A2A] last:border-0 ${
            l.read ? "" : "bg-[#D4AF37]/[0.03]"
          }`}
          data-testid={`lead-row-${l.id}`}
        >
          <div className="md:col-span-3">
            <div className="flex items-center gap-2">
              {!l.read && (
                <span className="w-2 h-2 bg-[#D4AF37] rounded-full" />
              )}
              <div className="text-white font-serif text-base">{l.name}</div>
            </div>
            <a
              href={`mailto:${l.email}`}
              className="block text-xs text-[#A3A3A3] hover:text-[#D4AF37] mt-1 flex items-center gap-1"
            >
              <Mail size={12} /> {l.email}
            </a>
            <div className="text-xs text-[#A3A3A3] flex items-center gap-1 mt-0.5">
              <Phone size={12} /> {l.phone}
            </div>
            <div className="text-[10px] text-[#737373] mt-2">
              {new Date(l.created_at).toLocaleString()}
            </div>
          </div>
          <div className="md:col-span-3 text-sm text-[#E5E5E5]">{l.subject}</div>
          <div className="md:col-span-4 text-sm text-[#A3A3A3] whitespace-pre-wrap">
            {l.message}
          </div>
          <div className="md:col-span-2 flex md:justify-end gap-2">
            {!l.read && (
              <button
                onClick={() => onRead(l.id)}
                className="inline-flex items-center gap-1 border border-[#2A2A2A] hover:border-[#D4AF37] hover:text-[#D4AF37] text-xs px-3 py-2"
                data-testid={`lead-read-${l.id}`}
              >
                <Check size={12} /> Mark read
              </button>
            )}
            <button
              onClick={() => onDelete(l.id)}
              className="inline-flex items-center gap-1 border border-[#2A2A2A] hover:border-red-500 hover:text-red-400 text-xs px-3 py-2"
              data-testid={`lead-delete-${l.id}`}
            >
              <Trash2 size={12} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function ProjectsManager({
  projects,
  form,
  setForm,
  editing,
  onSave,
  onEdit,
  onDelete,
  onReset,
}) {
  const upd = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));
  return (
    <div className="space-y-12">
      <form
        onSubmit={onSave}
        className="border border-[#2A2A2A] bg-[#141414] p-8"
        data-testid="project-form"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-serif text-2xl text-white">
            {editing ? "Edit Project" : "Add Project"}
          </h2>
          {editing && (
            <button
              type="button"
              onClick={onReset}
              className="text-xs text-[#A3A3A3] hover:text-white flex items-center gap-1"
              data-testid="project-form-cancel"
            >
              <X size={14} /> Cancel
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Title" value={form.title} onChange={upd("title")} testid="project-title" />
          <SelectField
            label="Sector"
            value={form.sector}
            onChange={upd("sector")}
            options={SECTOR_OPTS}
            testid="project-sector"
          />
          <Field label="Location" value={form.location} onChange={upd("location")} testid="project-location" />
          <Field label="Client" value={form.client} onChange={upd("client")} testid="project-client" />
          <Field label="Year" value={form.year} onChange={upd("year")} testid="project-year" />
          <Field label="Image URL" value={form.image_url} onChange={upd("image_url")} testid="project-image" />
          <label className="md:col-span-2 block">
            <span className="block text-[11px] uppercase tracking-[0.25em] text-[#D4AF37] mb-2">
              Description
            </span>
            <textarea
              rows={4}
              value={form.description}
              onChange={upd("description")}
              className="w-full bg-[#0A0A0A] border border-[#2A2A2A] text-white px-4 py-3 outline-none focus:border-[#D4AF37]"
              data-testid="project-description"
            />
          </label>
          <label className="md:col-span-2 inline-flex items-center gap-3 text-sm text-[#A3A3A3]">
            <input
              type="checkbox"
              checked={form.published}
              onChange={upd("published")}
              data-testid="project-published"
              className="accent-[#D4AF37]"
            />
            Published on public site
          </label>
        </div>

        <button
          type="submit"
          className="mt-8 inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#E5C665] text-black px-6 py-3 text-sm"
          data-testid="project-form-submit"
        >
          <Plus size={14} /> {editing ? "Save changes" : "Add Project"}
        </button>
      </form>

      {projects.length === 0 ? (
        <div
          className="border border-[#2A2A2A] bg-[#141414] p-16 text-center text-[#A3A3A3]"
          data-testid="projects-empty"
        >
          No projects yet. Add your first one above.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#2A2A2A] border border-[#2A2A2A]">
          {projects.map((p) => (
            <div
              key={p.id}
              className="bg-[#0A0A0A] p-6 flex flex-col"
              data-testid={`project-card-${p.id}`}
            >
              {p.image_url && (
                <div className="aspect-video mb-4 overflow-hidden border border-white/5">
                  <img
                    src={p.image_url}
                    alt={p.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-1 flex items-center justify-between">
                <span>{p.sector}</span>
                <span
                  className={`text-[9px] ${
                    p.published ? "text-emerald-400" : "text-[#737373]"
                  }`}
                >
                  {p.published ? "PUBLISHED" : "DRAFT"}
                </span>
              </div>
              <h3 className="font-serif text-xl text-white mb-1">{p.title}</h3>
              <div className="text-xs text-[#A3A3A3] mb-3">
                {p.location}
                {p.client ? ` · ${p.client}` : ""}
                {p.year ? ` · ${p.year}` : ""}
              </div>
              <p className="text-sm text-[#A3A3A3] flex-1 line-clamp-4">
                {p.description}
              </p>
              <div className="flex gap-2 mt-5 pt-4 border-t border-[#2A2A2A]">
                <button
                  onClick={() => onEdit(p)}
                  className="flex-1 inline-flex items-center justify-center gap-1 border border-[#2A2A2A] hover:border-[#D4AF37] hover:text-[#D4AF37] text-xs px-3 py-2"
                  data-testid={`project-edit-${p.id}`}
                >
                  <Pencil size={12} /> Edit
                </button>
                <button
                  onClick={() => onDelete(p.id)}
                  className="inline-flex items-center gap-1 border border-[#2A2A2A] hover:border-red-500 hover:text-red-400 text-xs px-3 py-2"
                  data-testid={`project-delete-${p.id}`}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.25em] text-[#D4AF37] mb-2">
        {label}
      </span>
      <input
        value={value}
        onChange={onChange}
        className="w-full bg-[#0A0A0A] border border-[#2A2A2A] text-white px-4 py-3 outline-none focus:border-[#D4AF37]"
        data-testid={testid}
      />
    </label>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.25em] text-[#D4AF37] mb-2">
        {label}
      </span>
      <select
        value={value}
        onChange={onChange}
        className="w-full bg-[#0A0A0A] border border-[#2A2A2A] text-white px-4 py-3 outline-none focus:border-[#D4AF37]"
        data-testid={testid}
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}
